// Every page in the CRM and the help that belongs to it. Each screen has a help
// key; the Help button opens the articles tagged with that key. A test walks the
// router and the link pages and fails if a page has no key, or a key has no
// article for the people who can reach it, so a new screen can't ship without help.
// Pure data: the staff app, the link app and the API all import it.

import { LINK_PAGES as LINK_PAGE_KEYS } from "./link-pages.ts";

export type Audience = "staff" | "platform" | "candidate" | "client";
export const AUDIENCES: readonly Audience[] = ["staff", "platform", "candidate", "client"];

export interface HelpPage {
	/** How the page is named in "Help with …". */
	title: string;
	/** Who can open this page. */
	audience: Audience;
}

export const HELP_PAGES = {
	// Signing in and getting around
	"sign-in": { title: "signing in", audience: "staff" },
	"choose-agency": { title: "choosing an agency", audience: "staff" },
	"answer-ssg": { title: "answering SSG", audience: "staff" },
	account: { title: "your account", audience: "staff" },
	home: { title: "Home", audience: "staff" },
	tasks: { title: "Tasks", audience: "staff" },
	help: { title: "Help", audience: "staff" },
	upload: { title: "uploading a photo", audience: "staff" },
	design: { title: "the design kit", audience: "staff" },
	// Candidates and clients
	candidates: { title: "Candidates", audience: "staff" },
	"candidate-form": { title: "adding a candidate", audience: "staff" },
	"candidate-record": { title: "a candidate's record", audience: "staff" },
	clients: { title: "Clients", audience: "staff" },
	"client-form": { title: "adding a client", audience: "staff" },
	"client-record": { title: "a client's record", audience: "staff" },
	// Work
	bookings: { title: "Bookings", audience: "staff" },
	"booking-new": { title: "a new booking", audience: "staff" },
	booking: { title: "a booking", audience: "staff" },
	plan: { title: "the week plan", audience: "staff" },
	timesheets: { title: "Timesheets", audience: "staff" },
	vacancies: { title: "Permanent jobs", audience: "staff" },
	vacancy: { title: "a permanent job", audience: "staff" },
	// Money
	rates: { title: "Rate cards", audience: "staff" },
	"rate-card": { title: "a rate card", audience: "staff" },
	payroll: { title: "Payroll", audience: "staff" },
	reports: { title: "Reports", audience: "staff" },
	board: { title: "the wall board", audience: "staff" },
	// Compliance
	compliance: { title: "checking documents", audience: "staff" },
	"document-settings": { title: "Documents you need", audience: "staff" },
	forms: { title: "Forms", audience: "staff" },
	"form-builder": { title: "building a form", audience: "staff" },
	agreements: { title: "Things people sign", audience: "staff" },
	// Set-up
	imports: { title: "importing a spreadsheet", audience: "staff" },
	team: { title: "Your team", audience: "staff" },
	ways: { title: "Ways of working", audience: "staff" },
	access: { title: "Access and exports", audience: "staff" },
	// SSG
	platform: { title: "All agencies", audience: "platform" },
	"platform-feedback": { title: "Feedback and problems", audience: "platform" },
	// Candidate links
	"link-timesheet": { title: "sending your hours", audience: "candidate" },
	"link-documents": { title: "adding your documents", audience: "candidate" },
	"link-form": { title: "filling in your form", audience: "candidate" },
	"link-join": { title: "signing up", audience: "candidate" },
	"link-offer": { title: "a shift offer", audience: "candidate" },
	"link-my-work": { title: "your work", audience: "candidate" },
	// Client links
	"link-approval": { title: "approving hours", audience: "client" },
	"link-request": { title: "asking for staff", audience: "client" },
	"link-signoff": { title: "agreeing rates", audience: "client" },
	"link-review": { title: "reviewing candidates", audience: "client" },
	"link-pack": { title: "meeting a new starter", audience: "client" },
} as const satisfies Record<string, HelpPage>;

export type HelpKey = keyof typeof HELP_PAGES;
export const HELP_KEYS = Object.keys(HELP_PAGES) as HelpKey[];
export const isHelpKey = (k: string): k is HelpKey => Object.hasOwn(HELP_PAGES, k);

/** Staff app: route path (as written in router.tsx) → help key. */
export const STAFF_ROUTES: Record<string, HelpKey> = {
	"/sign-in": "sign-in",
	"/sign-in/link": "sign-in",
	"/sign-in/new-email": "sign-in",
	"/choose-agency": "choose-agency",
	"/answer-ssg/$agencyId": "answer-ssg",
	"/account": "account",
	"/platform": "platform",
	"/platform/feedback": "platform-feedback",
	"/": "home",
	"/upload": "upload",
	"/help": "help",
	"/help/$slug": "help",
	"/tasks": "tasks",
	"/candidates": "candidates",
	"/candidates/new": "candidate-form",
	"/candidates/$id": "candidate-record",
	"/candidates/$id/edit": "candidate-form",
	"/clients": "clients",
	"/clients/new": "client-form",
	"/clients/$id": "client-record",
	"/clients/$id/edit": "client-form",
	"/imports": "imports",
	"/imports/$id": "imports",
	"/compliance": "compliance",
	"/settings/documents": "document-settings",
	"/settings/forms": "forms",
	"/settings/forms/$id": "form-builder",
	"/settings/agreements": "agreements",
	"/rates": "rates",
	"/rates/$id": "rate-card",
	"/reports": "reports",
	"/board": "board",
	"/settings/team": "team",
	"/payroll": "payroll",
	"/vacancies": "vacancies",
	"/vacancies/$id": "vacancy",
	"/settings/ways": "ways",
	"/settings/access": "access",
	"/bookings": "bookings",
	"/bookings/new": "booking-new",
	"/bookings/$id": "booking",
	"/plan": "plan",
	"/timesheets": "timesheets",
	"/design": "design",
};

export { linkAudience } from "./link-pages.ts";
/** Link app: first path segment → help key (see link-pages.ts). */
export const LINK_PAGES: Record<string, HelpKey> = LINK_PAGE_KEYS;

/** The help key for a matched staff route, falling back to Home. */
export function staffHelpKey(routePath: string | undefined): HelpKey {
	if (!routePath) return "home";
	const clean = routePath.length > 1 ? routePath.replace(/\/$/, "") : routePath;
	return STAFF_ROUTES[clean] ?? "home";
}
